// src/components/analytics/PlanVsActualChart.js
import React from 'react';
import { Box, Paper, Typography, Card, CardContent } from '@mui/material';
import { Assessment, CompareArrows } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const PlanVsActualChart = ({ plan, actualData, selectedYear, t }) => {
  if (!plan) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', height: 400 }}>
        <Typography variant="h6" color="text.secondary">
          {t('analytics_no_plan_selected')}
        </Typography>
      </Paper>
    );
  }

  // Підготовка даних для порівняння
  const chartData = actualData.map((monthData, index) => {
    const planned = plan.monthlyBreakdown?.[index] || 0;
    const actual = monthData.total;
    const deviation = planned > 0 ? ((actual - planned) / planned) * 100 : 0;
    
    return {
      name: t(`month_${index}`),
      planned: Math.round(planned),
      actual: Math.round(actual),
      deviation: Math.round(deviation),
      month: index + 1
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const planned = payload.find(p => p.dataKey === 'planned')?.value || 0;
      const actual = payload.find(p => p.dataKey === 'actual')?.value || 0;
      const deviation = planned > 0 ? ((actual - planned) / planned) * 100 : 0;

      return (
        <Card variant="outlined">
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {label}
            </Typography>
            <Typography variant="body2" color="#8884d8">
              {t('analytics_planned')}: {Math.round(planned).toLocaleString()} {t('analytics_units_kg')}
            </Typography>
            <Typography variant="body2" color="#82ca9d">
              {t('analytics_actual')}: {Math.round(actual).toLocaleString()} {t('analytics_units_kg')}
            </Typography>
            <Typography 
              variant="body2" 
              color={deviation >= 0 ? 'success.main' : 'error.main'}
            >
              {t('analytics_deviation')}: {Math.round(deviation)}%
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, height: 500 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <CompareArrows color="primary" />
        <Typography variant="h6">
          {t('analytics_plan_vs_actual')} {selectedYear}
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 12 }}
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar 
            dataKey="planned" 
            fill="#8884d8" 
            name={t('analytics_planned')}
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="actual" 
            fill="#82ca9d" 
            name={t('analytics_actual')}
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>

      <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2">
          {t('analytics_performance_summary')}: {getPerformanceSummary(chartData, t)}
        </Typography>
      </Box>
    </Paper>
  );
};

const getPerformanceSummary = (data, t) => {
  const completedMonths = data.filter(d => d.planned > 0);
  if (completedMonths.length === 0) return t('analytics_no_completed_months');

  const onTargetMonths = completedMonths.filter(d => d.actual >= d.planned * 0.9);
  const successRate = (onTargetMonths.length / completedMonths.length) * 100;

  if (successRate >= 80) return t('analytics_excellent_performance');
  if (successRate >= 60) return t('analytics_good_performance');
  if (successRate >= 40) return t('analytics_fair_performance');
  return t('analytics_needs_improvement');
};

export default PlanVsActualChart;